import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { validSections } from './avl-cart-state.js';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #refreshPromise = null;
  #refreshController = null;
  #authoritativeAt = 0;
  #opening = false;
  #openGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('avl:drawer-open', this.#otherDrawerOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#refreshOnOpen);
    window.addEventListener('pageshow', this.#refreshOnPageShow);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    this.#openGeneration++;
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('avl:drawer-open', this.#otherDrawerOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#refreshOnOpen);
    window.removeEventListener('pageshow', this.#refreshOnPageShow);
    this.#refreshController?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // CartAddEvent and CartUpdateEvent share a name. A read-only restoration
    // must update counts without opening a drawer the customer left closed.
    if (event.detail.data?.source !== 'cart-drawer-refresh') {
      this.#refreshController?.abort();
    }
    if (event.detail.data?.sections && Object.values(event.detail.data.sections).some(html => typeof html === 'string')) {
      this.#authoritativeAt = Date.now();
    }
    if (event instanceof CartAddEvent && this.hasAttribute('auto-open')) {
      this.open();
    }
    const absoluteCount = event.detail.resource?.item_count;
    queueMicrotask(() => this.#syncCartState(absoluteCount));
  };

  #refreshOnOpen = () => {
    this.#opening = false;
    // Adds can arrive while the modal is closed. Announce the newly rendered
    // total when it becomes visible, including the fast path that skips a read.
    this.#syncCartState();
    // A successful bundled add/change already supplied fresh native HTML.
    if (Date.now() - this.#authoritativeAt < 1500) return;
    this.refreshCart();
  };

  #syncCartState(absoluteCount) {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const count = Number.isInteger(absoluteCount) ? absoluteCount : Number(this.querySelector('[ref="cartItemCount"]')?.textContent || 0);
    dialog.classList.toggle('cart-drawer--empty', count === 0);
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
    this.#updateStickyState();
    this.#announceCartCount(count);
  }

  #refreshOnPageShow = (event) => {
    if (event.persisted) this.refreshCart();
  };

  /**
   * Read fresh native cart state after cached navigation or opening the drawer.
   * The read never mutates quantities, opens checkout, or retries a cart write.
   * @returns {Promise<void> | null}
   */
  refreshCart() {
    if (this.#refreshPromise) return this.#refreshPromise;
    // A pending native/custom mutation will supply its own authoritative event.
    if (globalThis.__avlCartMutation || document.querySelector('cart-items-component.cart-items-disabled')) return null;
    const items = this.querySelector('cart-items-component');
    const content = items?.querySelector('.cart-drawer__content');
    // The drawer belongs to the shared header group. Page-specific cart
    // sections can be unavailable outside their own template context.
    const ids = [items?.dataset.sectionId].filter(Boolean);
    if (!items || !ids.length) return null;
    // Serialize the read/render window with custom writes as well. Otherwise
    // a write can start after the initial guard and be overwritten by old HTML.
    const refreshLock = {};
    globalThis.__avlCartMutation = refreshLock;

    const controller = new AbortController();
    this.#refreshController = controller;
    const timeout = setTimeout(() => controller.abort('timeout'), 15000);
    const status = document.createElement('div');
    status.setAttribute('role', 'status');
    status.style.padding = '24px';
    const message = document.createElement('p');
    message.textContent = 'Henter din kurv…';
    status.append(message);
    items.querySelector('[data-cart-refresh-status]')?.remove();
    status.dataset.cartRefreshStatus = '';
    items.append(status);
    if (content) content.inert = true;
    const checkout = this.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = true;
    this.refs.dialog.setAttribute('aria-busy', 'true');
    let failed = false;
    let stage = 'cart-read';

    this.#refreshPromise = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const options = { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } };
        const cartResponse = await fetch(`${root}cart.js`, options);
        if (!cartResponse.ok) throw new Error(`Cart read failed (${cartResponse.status})`);
        const cart = await cartResponse.json();
        if (!Number.isInteger(cart.item_count) || !Array.isArray(cart.items)) throw new Error('Invalid cart response');

        // Bypass both browser and section-renderer caches while retaining the
        // current locale and unpublished-theme preview query parameters. The
        // shared header renders from the locale root, not product context.
        const url = new URL(window.location.href);
        url.pathname = root;
        url.hash = '';
        url.searchParams.delete('variant');
        url.searchParams.delete('selling_plan');
        url.searchParams.delete('view');
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', ids.join(','));
        url.searchParams.set('_avl_cart_refresh', String(Date.now()));
        stage = 'section-read';
        const sectionResponse = await fetch(url.toString(), options);
        if (!sectionResponse.ok) throw new Error(`Section read failed (${sectionResponse.status})`);
        stage = 'section-parse';
        const sections = await sectionResponse.json();
        stage = 'section-count';
        if (!validSections({ ...cart, sections }, ids)) throw new Error('Cart section unavailable or mismatched');
        if (controller.signal.aborted || !this.isConnected || globalThis.__avlCartMutation !== refreshLock) return;
        delete globalThis.__avlCartMutation;
        // Native cart-items components morph these sections; cart-icon receives
        // an absolute count because this is a CartUpdateEvent, not an add.
        this.dispatchEvent(new CartUpdateEvent(cart, this.id || 'cart-drawer', {
          source: 'cart-drawer-refresh', itemCount: cart.item_count, sections,
        }));
        this.#announceCartCount(cart.item_count);
        this.#updateStickyState();
      } catch (error) {
        if (controller.signal.aborted && controller.signal.reason !== 'timeout') return;
        failed = true;
        console.warn('[Avl cart refresh]', stage, error.message);
        message.textContent = 'Kurven kunne ikke hentes. Dine varer er gemt. Prøv igen.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button';
        retry.style.marginTop = '16px';
        retry.textContent = 'Hent kurven igen';
        retry.addEventListener('click', () => this.refreshCart(), { once: true });
        status.append(retry);
      } finally {
        clearTimeout(timeout);
        if (globalThis.__avlCartMutation === refreshLock) delete globalThis.__avlCartMutation;
        if (!failed) {
          status.remove();
          if (content?.isConnected) content.inert = false;
          if (checkout?.isConnected && !globalThis.__avlCartMutation) checkout.disabled = checkout.dataset.avlUnavailable === 'true';
        }
        this.refs.dialog?.removeAttribute('aria-busy');
        this.#refreshController = null;
        this.#refreshPromise = null;
      }
    })();
    return this.#refreshPromise;
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    const message = `${Theme.translations.cart_count}: ${cartCount}`;
    if (liveRegion.textContent !== message) liveRegion.textContent = message;
  }

  #otherDrawerOpen = (event) => {
    if (event.detail?.kind === 'cart') return;
    this.#openGeneration++;
    this.#opening = false;
    if (this.refs.dialog?.open) event.detail.waits?.push(this.closeDialog());
  };

  async open() {
    if (this.refs.dialog?.open || this.#opening) return;
    this.#opening = true;
    const generation = ++this.#openGeneration;
    const detail = { kind: 'cart', source: this, waits: [] };
    window.dispatchEvent(new CustomEvent('avl:drawer-open', { detail }));
    await Promise.all(detail.waits);
    if (!this.isConnected || generation !== this.#openGeneration) { this.#opening = false; return; }
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
